import { useState, useEffect, useRef } from "react";
import { ChevronDown, Loader2, X } from "lucide-react";
import type { Bank } from "../../redux/services/Wallet";

interface BankDropdownProps {
  banks: Bank[];
  isLoading: boolean;
  isError: boolean;
  bankData: { bankName: string; bankCode?: string };
  handleInputChange: (e: any) => void;
  handleBlur: (
    e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>
  ) => void;
  errors: { bankName?: string };
  clearError?: (field: string) => void; // ✅ optional clearError function
}

const BankDropdown: React.FC<BankDropdownProps> = ({
  banks,
  isLoading,
  isError,
  bankData,
  handleInputChange,
  handleBlur,
  errors,
  clearError,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState<"top" | "bottom">(
    "bottom"
  );

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLDivElement | null>(null);

  // ✅ Detect position of dropdown (top or bottom)
  const detectDropdownPosition = () => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow > 200) setDropdownPosition("bottom");
    else if (spaceAbove > 250) setDropdownPosition("top");
    else setDropdownPosition(spaceBelow > spaceAbove ? "bottom" : "top");
  };

  // ✅ Recalculate on scroll/resize
  useEffect(() => {
    if (!open) return;
    detectDropdownPosition();
    const handleScrollOrResize = () => detectDropdownPosition();
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);
    return () => {
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [open]);

  // ✅ Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
        handleBlur({
          target: { name: "bankName", value: bankData.bankName },
        } as any);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [bankData.bankName, handleBlur]);

  // ✅ Toggle dropdown and clear error when opened
  const toggleDropdown = () => {
    if (isLoading || isError) return;
    setOpen((prev) => !prev);
    setSearchQuery("");
    detectDropdownPosition();
    clearError?.("bank"); // ✅ clear error immediately when user interacts
  };

  // ✅ Clear selection
  const clearSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleInputChange({
      target: { name: "bankData", value: { bankName: "", bankCode: "" } },
    });
    setSearchQuery("");
    setOpen(false);
    clearError?.("bank"); // ✅ also clear error when clearing selection
  };

  // ✅ Filter banks — Array.isArray, not just a null check, since a bad API
  // response shape can hand this a truthy non-array (e.g. a pagination
  // wrapper object), which `?? []` alone wouldn't catch and .filter() would
  // then crash on.
  const filteredBanks = Array.isArray(banks)
    ? banks.filter((bank) =>
        bank.name.toLowerCase().includes(searchQuery.toLowerCase().trim())
      )
    : [];

  // ✅ Select bank and clear error immediately
  const handleSelectBank = (bank: Bank) => {
    handleInputChange({
      target: {
        name: "bankData",
        value: { bankName: bank.name, bankCode: bank.code },
      },
    });
    setOpen(false);
    handleBlur({
      target: { name: "bankName", value: bank.name },
    } as any);
    clearError?.("bank"); // ✅ clears red message immediately
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <label className="block text-[16px] font-medium text-fleetdark mb-2">
        Bank Name:<span className="text-red-500">*</span>
      </label>

      {/* Input Trigger */}
      <div
        ref={inputRef}
        onClick={toggleDropdown}
        className={`flex items-center justify-between w-full px-4 py-3 border ${
          errors.bankName ? "border-red-500" : "border-gray-300"
        } rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-all`}
      >
        {isLoading ? (
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading banks...
          </div>
        ) : isError ? (
          <span className="text-red-500 text-sm">Failed to load banks</span>
        ) : (
          <div className="flex items-center justify-between w-full">
            <span
              className={`text-gray-700 truncate ${
                !bankData.bankName ? "text-gray-400" : ""
              }`}
            >
              {bankData.bankName || "Choose Bank"}
            </span>
            <div className="flex items-center gap-2">
              {bankData.bankName && (
                <button
                  onClick={clearSelection}
                  className="p-1 hover:bg-gray-200 rounded-full"
                  title="Clear selection"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              )}
              <ChevronDown
                className={`w-5 h-5 text-gray-500 transition-transform ${
                  open ? "rotate-180" : ""
                }`}
              />
            </div>
          </div>
        )}
      </div>

      {/* Dropdown List */}
      {open && !isLoading && !isError && (
        <div
          className={`absolute z-20 w-full bg-white border border-gray-200 rounded-lg shadow-md max-h-64 overflow-y-auto ${
            dropdownPosition === "top" ? "bottom-full mb-2" : "top-full mt-2"
          }`}
        >
          {/* Search box */}
          <div className="p-2 sticky top-0 bg-white border-b">
            <input
              type="text"
              placeholder="Search bank..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                clearError?.("bank"); // ✅ clear error on typing immediately
              }}
              onClick={(e) => e.stopPropagation()}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-[30px] outline-none"
            />
          </div>

          {/* Bank list */}
          <ul>
            {filteredBanks.length === 0 ? (
              <li className="px-4 py-2 text-gray-500 text-sm">
                No results found
              </li>
            ) : (
              filteredBanks.slice(0, 100).map((bank) => (
                <li
                  key={bank.code}
                  onClick={() => handleSelectBank(bank)}
                  className={`px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm ${
                    bankData.bankName === bank.name ? "bg-gray-50" : ""
                  }`}
                >
                  {bank.name}
                </li>
              ))
            )}
          </ul>
        </div>
      )}

      {/* Error */}
      {errors.bankName && (
        <p className="text-red-500 text-sm mt-1">{errors.bankName}</p>
      )}
    </div>
  );
};

export default BankDropdown;
